// FormularyTab.tsx — Hospital Formulary admin (admin:formulary). What this
// hospital prefers/allows/restricts for a clinical drug, and which
// commercial products it recommends, ranked. See
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §5.6-§5.7, §13-§15, §23.
//
// A drug absent from this list is implicitly "allowed, unflagged" — this
// tab is only for drugs an admin actively wants to configure, not a mirror
// of every drug_master row.
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFormularyList, getFormularyEntry, getUnconfiguredDrugs, getProductsForDrug,
  createFormularyEntry, updateFormularyEntry, createMedicineForDrug,
} from '../api';
import { downloadFormularyTemplate, uploadFormularyExcel } from '@/api';
import type { FormularyEntry, FormularyProduct, UnconfiguredDrug, FormularyBulkUploadResult } from '../types';
import type { FormularySaveInput } from '../api';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, NativeSelect } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';

const STATUS_META: Record<string, { label: string; bg: string; text: string }> = {
  preferred: { label: 'Preferred', bg: '#D1FAE5', text: '#065F46' },
  allowed: { label: 'Allowed', bg: '#F3F4F6', text: '#374151' },
  restricted: { label: 'Restricted', bg: '#FEF3C7', text: '#92400E' },
  non_formulary: { label: 'Non-formulary', bg: '#FEE2E2', text: '#991B1B' },
};
const PRODUCT_STATUS_META: Record<string, { label: string; bg: string; text: string }> = {
  preferred: { label: '⭐ Preferred', bg: '#D1FAE5', text: '#065F46' },
  approved: { label: 'Approved', bg: '#F3F4F6', text: '#374151' },
  restricted: { label: 'Restricted', bg: '#FEF3C7', text: '#92400E' },
  not_recommended: { label: 'Not recommended', bg: '#FEE2E2', text: '#991B1B' },
};

type ProductDraft = { product_id: number; status: string; priority: number; allow_substitution: boolean; active: boolean; notes: string;
  generic_name: string; brand_name: string | null; strength: string | null };

function productsToDraft(products: FormularyProduct[] = []): ProductDraft[] {
  return products.map((p) => ({
    product_id: p.product_id, status: p.status, priority: p.priority, allow_substitution: p.allow_substitution,
    active: p.active, notes: p.notes || '', generic_name: p.generic_name, brand_name: p.brand_name, strength: p.strength,
  }));
}

function Editor({ entry, drugId, drugLabel, genericName, onSaved, onCancel }: {
  entry: FormularyEntry | null; drugId: number; drugLabel: string; genericName: string; onSaved: () => void; onCancel: () => void;
}) {
  const [status, setStatus] = useState<string>(entry?.status || 'allowed');
  const [restriction, setRestriction] = useState(entry?.prescribing_restriction || '');
  const [allowSub, setAllowSub] = useState(entry?.allow_substitution ?? true);
  const [notes, setNotes] = useState(entry?.notes || '');
  const [reviewReason, setReviewReason] = useState('');
  const [products, setProducts] = useState<ProductDraft[]>(productsToDraft(entry?.products));
  const [candidates, setCandidates] = useState<Array<{ id: number; brand_name: string | null; strength: string | null }> | null>(null);
  const [addingProductId, setAddingProductId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [onboarding, setOnboarding] = useState(false);
  const [newBrand, setNewBrand] = useState({ brand_name: '', strength: '', manufacturer_name: '', hsn_code: '' });
  const [onboardingBusy, setOnboardingBusy] = useState(false);

  useEffect(() => {
    getProductsForDrug(drugId).then((rows) => setCandidates(rows)).catch((e: any) => setError(e.message));
  }, [drugId]);

  const availableToAdd = (candidates || []).filter((c) => !products.some((p) => p.product_id === c.id));

  const addProduct = () => {
    const id = Number(addingProductId);
    const cand = (candidates || []).find((c) => c.id === id);
    if (!cand) return;
    setProducts((prev) => [...prev, {
      product_id: id, status: 'approved', priority: prev.length + 1, allow_substitution: true, active: true, notes: '',
      generic_name: '', brand_name: cand.brand_name, strength: cand.strength,
    }]);
    setAddingProductId('');
  };
  const removeProduct = (productId: number) => setProducts((prev) => prev.filter((p) => p.product_id !== productId));

  // "+ New brand" onboarding fast path (§22) — creates the product with
  // drug_id pre-set (this Editor already knows exactly which clinical drug
  // it is), so it's created already resolved, and adds it straight into the
  // formulary draft in the same motion — no trip through the normalization
  // review queue, unlike a brand received via Goods Receipt (§21.1), where
  // the receiving pharmacist may not know the clinical drug at all.
  const createNewBrand = async () => {
    setOnboardingBusy(true); setError('');
    try {
      const created = await createMedicineForDrug({
        drug_id: drugId,
        generic_name: genericName,
        brand_name: newBrand.brand_name.trim() || undefined,
        strength: newBrand.strength.trim() || undefined,
        manufacturer_name: newBrand.manufacturer_name.trim() || undefined,
        hsn_code: newBrand.hsn_code.trim() || undefined,
      });
      setCandidates((prev) => [...(prev || []), created]);
      setProducts((prev) => [...prev, {
        product_id: created.id, status: 'approved', priority: prev.length + 1, allow_substitution: true, active: true, notes: '',
        generic_name: created.generic_name, brand_name: created.brand_name, strength: created.strength,
      }]);
      setOnboarding(false);
      setNewBrand({ brand_name: '', strength: '', manufacturer_name: '', hsn_code: '' });
    } catch (e: any) { setError(e.message); }
    setOnboardingBusy(false);
  };
  const updateProduct = (productId: number, patch: Partial<ProductDraft>) =>
    setProducts((prev) => prev.map((p) => (p.product_id === productId ? { ...p, ...patch } : p)));

  const save = async () => {
    setSaving(true); setError('');
    const data: FormularySaveInput = {
      status, prescribing_restriction: restriction.trim() || undefined, allow_substitution: allowSub,
      notes: notes.trim() || undefined,
      products: products.map((p) => ({
        product_id: p.product_id, status: p.status, priority: p.priority, allow_substitution: p.allow_substitution, active: p.active,
        notes: p.notes.trim() || undefined,
      })),
    };
    if (reviewReason.trim()) data.review_reason = reviewReason.trim();
    try {
      if (entry) await updateFormularyEntry(entry.id, data);
      else await createFormularyEntry(drugId, data);
      onSaved();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  return (
    <Card className="p-4 mt-2" style={{ background: '#FAFAFA' }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>{drugLabel}</div>
      {error && <div style={{ marginBottom: 10, padding: '6px 10px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={{ fontSize: 11, color: '#6B7280', display: 'block', marginBottom: 4 }}>Formulary status</label>
          <NativeSelect value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="preferred">Preferred</option>
            <option value="allowed">Allowed</option>
            <option value="restricted">Restricted</option>
            <option value="non_formulary">Non-formulary</option>
          </NativeSelect>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
          <Checkbox checked={allowSub} onCheckedChange={() => setAllowSub((v) => !v)} />
          <span style={{ fontSize: 12.5 }}>Substitution allowed for this drug</span>
        </div>
      </div>

      {(status === 'restricted' || status === 'non_formulary') && (
        <Textarea placeholder="Prescribing restriction (shown to doctors as advisory context)" value={restriction}
          onChange={(e) => setRestriction(e.target.value)} className="mb-3 h-[52px] text-xs" />
      )}
      <Textarea placeholder="Internal notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="mb-3 h-[52px] text-xs" />
      <Input placeholder="Reason for this review (optional — stamps reviewed_by/reviewed_at for the audit trail)"
        value={reviewReason} onChange={(e) => setReviewReason(e.target.value)} className="mb-4 text-xs" />

      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Recommended products</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 10 }}>
        {products.map((p) => {
          const meta = PRODUCT_STATUS_META[p.status] || PRODUCT_STATUS_META.approved!;
          return (
            <div key={p.product_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', padding: '6px 10px' }}>
              <span style={{ flex: 1 }}>{p.brand_name || '(unbranded)'} {p.strength ? `— ${p.strength}` : ''}</span>
              <NativeSelect value={p.status} onChange={(e) => updateProduct(p.product_id, { status: e.target.value })} style={{ width: 150, fontSize: 11 }}>
                <option value="preferred">Preferred</option>
                <option value="approved">Approved</option>
                <option value="restricted">Restricted</option>
                <option value="not_recommended">Not recommended</option>
              </NativeSelect>
              <Input type="number" value={p.priority} onChange={(e) => updateProduct(p.product_id, { priority: Number(e.target.value) || 1 })} style={{ width: 56 }} className="text-xs" />
              <label style={{ display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                <Checkbox checked={p.allow_substitution} onCheckedChange={() => updateProduct(p.product_id, { allow_substitution: !p.allow_substitution })} />
                <span style={{ fontSize: 10.5, color: '#6B7280' }}>substitutable</span>
              </label>
              <span className="badge" style={{ background: meta.bg, color: meta.text, fontSize: 10 }}>{meta.label}</span>
              <Button variant="ghost" size="sm" onClick={() => removeProduct(p.product_id)}>✕</Button>
            </div>
          );
        })}
        {products.length === 0 && <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>No recommended products yet.</div>}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: onboarding ? 8 : 16, flexWrap: 'wrap' }}>
        {availableToAdd.length > 0 && (
          <>
            <NativeSelect value={addingProductId} onChange={(e) => setAddingProductId(e.target.value)} style={{ flex: 1, minWidth: 180 }}>
              <option value="">Add a product…</option>
              {availableToAdd.map((c) => <option key={c.id} value={c.id}>{c.brand_name || '(unbranded)'}{c.strength ? ` — ${c.strength}` : ''}</option>)}
            </NativeSelect>
            <Button variant="primary" size="sm" disabled={!addingProductId} onClick={addProduct}>+ Add</Button>
          </>
        )}
        {!onboarding && <Button variant="ghost" size="sm" onClick={() => setOnboarding(true)}>+ New brand</Button>}
      </div>
      {onboarding && (
        <div style={{ marginBottom: 16, padding: 10, border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', background: '#fff' }}>
          <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 8 }}>
            Onboard a new brand of <b>{genericName}</b> — created and added to this formulary in one step, no review queue.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
            <Input placeholder="Brand name" value={newBrand.brand_name} onChange={(e) => setNewBrand((b) => ({ ...b, brand_name: e.target.value }))} className="text-xs" />
            <Input placeholder="Strength (if it differs)" value={newBrand.strength} onChange={(e) => setNewBrand((b) => ({ ...b, strength: e.target.value }))} className="text-xs" />
            <Input placeholder="Manufacturer" value={newBrand.manufacturer_name} onChange={(e) => setNewBrand((b) => ({ ...b, manufacturer_name: e.target.value }))} className="text-xs" />
            <Input placeholder="HSN code" value={newBrand.hsn_code} onChange={(e) => setNewBrand((b) => ({ ...b, hsn_code: e.target.value }))} className="text-xs" />
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="primary" size="sm" disabled={onboardingBusy} onClick={createNewBrand}>{onboardingBusy ? 'Creating…' : 'Create & add'}</Button>
            <Button variant="ghost" size="sm" onClick={() => { setOnboarding(false); setError(''); }}>Cancel</Button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button variant="success" size="sm" disabled={saving} onClick={save}>{saving ? 'Saving…' : entry ? 'Save changes' : 'Create formulary entry'}</Button>
        <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
}

function AddDrugPicker({ onPick, onCancel }: { onPick: (d: UnconfiguredDrug) => void; onCancel: () => void }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<UnconfiguredDrug[]>([]);
  useEffect(() => {
    const t = setTimeout(() => { getUnconfiguredDrugs(q).then(setResults).catch(() => setResults([])); }, 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <Card className="p-3.5 mt-2">
      <Input autoFocus placeholder="Search a clinical drug to add to the formulary…" value={q} onChange={(e) => setQ(e.target.value)} className="mb-2 text-xs" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 220, overflowY: 'auto' }}>
        {results.map((d) => (
          <button key={d.id} onClick={() => onPick(d)} style={{ textAlign: 'left', padding: '6px 10px', border: '1px solid #E5E7EB', borderRadius: 'var(--radius)', background: '#fff', fontSize: 12, cursor: 'pointer' }}>
            {d.canonical_name}
          </button>
        ))}
        {results.length === 0 && <div style={{ fontSize: 11.5, color: '#9CA3AF' }}>{q ? 'No unconfigured drug matches.' : 'Type to search drug_master entries with no formulary row yet.'}</div>}
      </div>
      <Button variant="ghost" size="sm" className="mt-2" onClick={onCancel}>Cancel</Button>
    </Card>
  );
}

export default function FormularyTab() {
  const [entries, setEntries] = useState<FormularyEntry[] | null>(null);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<{ mode: 'edit'; entry: FormularyEntry } | { mode: 'new'; drug: UnconfiguredDrug } | null>(null);
  const [picking, setPicking] = useState(false);
  const [detail, setDetail] = useState<FormularyEntry | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<FormularyBulkUploadResult | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => getFormularyList().then(setEntries).catch((e: any) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true); setError(''); setUploadResult(null);
    try {
      const result = await uploadFormularyExcel(file);
      setUploadResult(result);
      load();
    } catch (e: any) { setError(e.message); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const openEdit = async (row: FormularyEntry) => {
    setError('');
    try {
      const full = await getFormularyEntry(row.id);
      setDetail(full);
      setExpanded({ mode: 'edit', entry: full });
    } catch (e: any) { setError(e.message); }
  };

  const saved = () => { setExpanded(null); setDetail(null); setPicking(false); load(); };

  if (!entries) return <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading formulary…</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.6 }}>
        What this hospital prefers, allows, restricts, or excludes for a clinical drug, and which commercial
        products it recommends. A drug not listed here is implicitly allowed and unflagged — nothing here ever
        blocks a doctor from prescribing; formulary status and out-of-stock are always advisory only.
      </div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      {uploadResult && (
        <div style={{ marginBottom: 14, padding: '10px 12px', background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 'var(--radius)', fontSize: 12 }}>
          <div style={{ fontWeight: 700, color: '#065F46', marginBottom: 4 }}>
            Processed {uploadResult.processed} row{uploadResult.processed === 1 ? '' : 's'}: {uploadResult.created} created, {uploadResult.updated} updated,
            {uploadResult.newProducts > 0 ? ` ${uploadResult.newProducts} new brand${uploadResult.newProducts === 1 ? '' : 's'} created,` : ''} {uploadResult.failed} failed.
          </div>
          {uploadResult.failed > 0 && (
            <ul style={{ margin: 0, paddingLeft: 18, color: '#991B1B' }}>
              {uploadResult.results.filter((r) => r.status === 'error').map((r, i) => (
                <li key={i}>{r.sheet}{r.row ? ` row ${r.row}` : ''}: {r.message}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <Button variant="primary" size="sm" onClick={() => setPicking(true)}>+ Add drug to formulary</Button>
        <div style={{ flex: 1 }} />
        <Button variant="ghost" size="sm" onClick={() => downloadFormularyTemplate().catch((e: any) => setError(e.message))}>⬇ Download template</Button>
        <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={(e) => handleUpload(e.target.files?.[0])} />
        <Button variant="ghost" size="sm" disabled={uploading} loading={uploading} onClick={() => fileRef.current?.click()}>⬆ Upload Excel</Button>
      </div>
      {picking && <AddDrugPicker onCancel={() => setPicking(false)} onPick={(d) => { setPicking(false); setExpanded({ mode: 'new', drug: d }); }} />}
      {expanded?.mode === 'new' && (
        <Editor entry={null} drugId={expanded.drug.id} drugLabel={expanded.drug.canonical_name} genericName={expanded.drug.generic_name} onSaved={saved} onCancel={() => setExpanded(null)} />
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
        {entries.map((row) => {
          const meta = STATUS_META[row.status] || STATUS_META.allowed!;
          const isOpen = expanded?.mode === 'edit' && expanded.entry.id === row.id;
          return (
            <div key={row.id}>
              <Card className="p-3">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ fontSize: 13 }}>
                    <span style={{ fontWeight: 600 }}>{row.canonical_name}</span>
                    {row.product_count ? <span style={{ color: '#9CA3AF', fontSize: 11.5 }}> · {row.product_count} product{row.product_count === 1 ? '' : 's'}</span> : null}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className="badge" style={{ background: meta.bg, color: meta.text }}>{meta.label}</span>
                    <Button variant="primary" size="sm" onClick={() => (isOpen ? setExpanded(null) : openEdit(row))}>{isOpen ? 'Close' : 'Edit'}</Button>
                  </div>
                </div>
              </Card>
              {isOpen && detail && (
                <Editor entry={detail} drugId={detail.drug_id} drugLabel={detail.canonical_name} genericName={detail.generic_name} onSaved={saved} onCancel={() => setExpanded(null)} />
              )}
            </div>
          );
        })}
        {entries.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No formulary entries yet — every drug is currently allowed and unflagged.</div>}
      </div>
    </div>
  );
}
